/* eslint-disable react/jsx-props-no-spreading */
// import * as ReactDOMClient from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import { useCallback, useContext, useEffect, useRef } from 'react';
import LocationListingContext, { VIEW_MAP, type LocationListingContextProps, type LocationListingProps, VIEW_LISTING } from './LocationListingContext';
import ListingMap from './Map/ListingMap';
import LocationListingProvider from './LocationListingProvider';
import MapPanel from './Map/MapPanel';
import Switch from '2-core-elements/Switch/Switch';
import { getCleanFilters, getFilterQuery, getPageQueryURL, getQueryURL, getWhereValue, type WhereItem } from '3-components/ListingApp/ListingFilters/ListingFilterHelpers';
import { type LocationListingQuery, useLocationListingQuery } from 'cms/cms';
import { DEFAULT_FETCH_PARAMS, OPTMIZELY_GRAPHQL_BASE_URL } from 'cms/constants';
import { QueryClient, QueryClientProvider, keepPreviousData } from '@tanstack/react-query';
import { ListingError, ListingLoader } from '3-components/ListingApp/ListingUI/ListingUI';
import ListingHeader from '3-components/ListingApp/ListingHeader/ListingHeader';
import ListingContent from '3-components/ListingApp/ListingContent/ListingContent';
import { resolveLocationListing } from 'cms/resolvers/locationListingResolver';
import LocationCard from './LocationCard';
import ListingGroup from '3-components/ListingApp/ListingGroup/ListingGroup';
import type { ActiveFilter } from '3-components/ListingApp/ListingFilters/ListingFiltersTypes';


const LocationListing = (): JSX.Element => {
    const {
        locale,
        map,
        view,
        setView,
        label,
        activeItemId,
        setActiveItemId,
        listing,
        setActiveFilters,
        activeFilters,
        cms,
        page,
        setPage,
    } = useContext(LocationListingContext) as LocationListingContextProps;

    const updateFilters = (value: ActiveFilter[]): void => {
        setPage(0);
        setActiveFilters(value);
    };

    const activeViewSetting = view === VIEW_MAP ? map : listing;
    const { filterByAncestors } = activeViewSetting;
    const { search, dateacquired, ...restParams } = getFilterQuery(activeFilters, activeViewSetting.filters);
    const scrollToEl = useRef<HTMLDivElement>(null);

    const whereItems: WhereItem[] = [];
    if (search) {
        whereItems.push({
            type: 'search',
            field: '_fulltext',
            value: search,
        });
    }

    if (dateacquired) {
        whereItems.push({
            type: 'year',
            field: 'DateAcquired',
            value: dateacquired,
        });
    }

    if (filterByAncestors) {
        whereItems.push({
            type: 'ancestors',
            field: 'Ancestors',
            value: filterByAncestors,
        });
    }

    const where = getWhereValue(whereItems);

    const { data, error, isLoading, isError, isFetching } = useLocationListingQuery({
        endpoint: `${cms.apiUrl ? cms.apiUrl : OPTMIZELY_GRAPHQL_BASE_URL}${cms.apiKey}`,
        fetchParams: DEFAULT_FETCH_PARAMS,
    }, {
        locale,
        where,
        skip: 0,
        limit: 100,
        // skip: view === VIEW_MAP ? 0 : page * listing.itemsPerPage,
        // limit: view === VIEW_MAP ? 100 : listing.itemsPerPage,
        ...restParams,
    }, {
        placeholderData: keepPreviousData,
        staleTime: 1000 * 60 * 5,
        select: useCallback((result: LocationListingQuery) => {
            return resolveLocationListing(result, {
                emailLabel: label.emailLabel,
                telLabel: label.telLabel,
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []), // https://tkdodo.eu/blog/react-query-data-transformations
    });

    useEffect(() => {
        window.history.replaceState({}, '', getQueryURL(activeFilters, activeViewSetting.filters));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    useEffect(() => {
        if (scrollToEl?.current) {
            scrollToEl.current.scrollIntoView({ behavior: 'smooth' });
        }

        window.history.replaceState({}, '', getPageQueryURL(page));
    }, [page]);

    useEffect(() => {
        const restQuery = view === VIEW_MAP ? { view: undefined } : { view };
        window.history.replaceState({}, '', getQueryURL([], [], restQuery));
    }, [view]);

    if (isLoading) {
        return (
            <ListingLoader loadingMessage={label.loadingMessage} />
        );
    }

    if (isError || !data) {
        console.error(error);

        return (
            <ListingError errorMessage={label.errorMessage} />
        );
    }

    return (
        <div className={`location-listing-container container ${activeItemId ? 'pin-selected' : ''}`} ref={scrollToEl}>
            <div className="location-listing-header">
                <h2 className="location-listing-heading">{label.title}</h2>
                <div className="location-listing-utility">
                    <Switch
                        size="lg"
                        offLabel={label.mapViewSwitchTitle}
                        onLabel={label.listViewSwitchTitle}
                        checked={view === VIEW_LISTING}
                        id="location-listing-switch"
                        onChange={(nextChecked: boolean): void => {
                            const nextView = nextChecked ? VIEW_LISTING : VIEW_MAP;
                            const activeNextViewFilters = nextView === VIEW_LISTING ? listing.filters : map.filters;

                            // remove all unnecesary filters on query URL
                            window.history.replaceState({}, '', getQueryURL(getCleanFilters(activeViewSetting.filters || []), activeViewSetting.filters || []));

                            // set active filters for cleaned version of it
                            updateFilters(getCleanFilters(activeNextViewFilters || []));

                            // clear the active item id
                            setActiveItemId('');

                            // then change the view
                            setView(nextView);
                        }}
                    />
                </div>
            </div>
            {label.filterHeading && activeViewSetting.filters && (
                <ListingHeader
                    filters={activeViewSetting.filters}
                    activeFilters={activeFilters}
                    setActiveFilters={updateFilters}
                    title={label.filterHeading}
                    clearFilterLabel={label.clearFilterButton}
                />
            )}
            <div className="location-listing-content">
                {view === VIEW_MAP && (
                    <div className="map-layout">
                        <div className="map-column">
                            <ListingMap
                                apiKey={map.apiKey}
                                mapId={map.mapId}
                                items={data.mapItems}
                            />
                        </div>
                        <div className="map-info-column">
                            <div className="map-info-column-content">
                                <MapPanel items={data.mapItems} />
                            </div>
                        </div>
                    </div>
                )}
                {view === VIEW_LISTING && (
                    <ListingContent
                        isFetching={isFetching}
                        isNoItems={!data.cardGroups.length}
                        // pagination={{
                        //     itemsPerPage: listing.itemsPerPage,
                        //     page,
                        //     setPage,
                        //     totalItems: data.total,
                        // }}
                        loadingLabel={label.loadingMessage}
                        noItemsLabel={label.noItemMessage}
                        cols="1"
                    >
                        {data.cardGroups.map(({ title, cardItems }, groupIndex) => {
                            const groupKey = `${title}-${groupIndex}`;

                            return (
                                <ListingGroup key={groupKey} title={title}>
                                    {cardItems.map((props, i) => {
                                        const key = `${props.title}-${i}`;
                                        return (
                                            <LocationCard key={key} {...props} />
                                        );
                                    })}
                                </ListingGroup>
                            );
                        })}
                    </ListingContent>
                )}
            </div>
        </div>
    );
};

const queryClient = new QueryClient();

interface LocationListingInitProps {
    el: HTMLDivElement;
    props: LocationListingProps;
}

const LocationListingInit = ({ el, props }: LocationListingInitProps): void => {
    /* // Using ReactDOM forced it to use React 17,
    // The plugin have issue with React 18, flickering on the pin
    ReactDOM.render(
        <QueryClientProvider client={queryClient}>
            <LocationListingProvider {...props}>
                <LocationListing />
            </LocationListingProvider>
        </QueryClientProvider>,
        el
    ); */

    const root = createRoot(el);

    root.render(
        <QueryClientProvider client={queryClient}>
            <LocationListingProvider {...props}>
                <LocationListing />
            </LocationListingProvider>
        </QueryClientProvider>
    );
};

export default LocationListingInit;
